/**
 * React Query hooks for Quick Assessment.
 * React Query hooks for the corresponding entity.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";

export const NO_PROGRAMM_ID = "QUICK_ASSESSMENT";

export type QuickAssessmentResult = {
  guid: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  results: { [key: string]: any };
  submitted: boolean;
};

class QuickAssessmentAPI {
  async getAll(): Promise<QuickAssessmentResult> {
    const res = await fetch(
      `${import.meta.env.VITE_API_ENDPOINT}/v2/quickAssessment`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
          "Content-Type": "application/json",
        },
      }
    );
    // Return empty result if API returns error (e.g., 400, 404, 500)
    if (!res.ok) {
      console.warn(`Failed to fetch quick assessment: ${res.status}`);
      return {
        guid: NO_PROGRAMM_ID,
        results: {},
        submitted: false,
      };
    }
    const resultJson = await res.json();
    // The API returns the result directly, we add the guid for consistency
    return {
      guid: NO_PROGRAMM_ID,
      results: resultJson.results,
      submitted: resultJson.submitted,
    } as QuickAssessmentResult;
  }
}

const quickAssessmentApi = new QuickAssessmentAPI();

/**
 * Fetch quick assessment results.
 * @param enabled - Whether the query is enabled
 * @param refetchInterval - Interval in ms to refetch (for polling after submission)
 */
export function useQuickAssessmentResults(
  enabled = true,
  refetchInterval?: number | false
) {
  return useQuery({
    queryKey: queryKeys.quickAssessment.results,
    queryFn: async () => {
      return quickAssessmentApi.getAll();
    },
    enabled,
    refetchInterval,
  });
}

/**
 * Helper to check if quick assessment has been submitted
 */
export function useHasFilledOutQuickAssessment() {
  const { data } = useQuickAssessmentResults();
  return data?.submitted === true;
}

/**
 * Hook to invalidate quick assessment cache
 */
export function useInvalidateQuickAssessment() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.quickAssessment.results,
    });
  };
}

/**
 * Hook to reset quick assessment cache
 */
export function useResetQuickAssessmentCache() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.removeQueries({ queryKey: queryKeys.quickAssessment.results });
  };
}

export { quickAssessmentApi };

/**
 * Fetch quick assessment results directly (not a hook, for use in services).
 */
export async function fetchQuickAssessmentResultsQuery(): Promise<QuickAssessmentResult> {
  return quickAssessmentApi.getAll();
}
